<template>
  <div class="musubi-page dm-page">
    <header class="musubi-page__head">
      <div class="dm-head-row">
        <h1>Direct Mail - Review and submit <span class="dm-badge">New</span></h1>
        <span v-if="loaded && !isPaying" class="plan-pill plan-pill--free"><CygnusIcon name="info-circle" /> Free plan</span>
      </div>
      <p class="text-muted">Check the audience and artwork, then submit. Nothing prints until pricing is confirmed with you by email.</p>
    </header>

    <!-- Stepper -->
    <nav class="dm-steps" aria-label="Direct mail steps">
      <router-link :to="audienceLink" class="dm-step is-done">
        <span class="dm-step__num"><CygnusIcon name="check" /></span>
        <span class="dm-step__body"><span class="dm-step__title">Who gets mail</span><span class="dm-step__sub">Audience</span></span>
      </router-link>
      <router-link :to="designLink" class="dm-step is-done">
        <span class="dm-step__num"><CygnusIcon name="check" /></span>
        <span class="dm-step__body"><span class="dm-step__title">Design your mail piece</span><span class="dm-step__sub">{{ formatLabel }}</span></span>
      </router-link>
      <div class="dm-step is-current">
        <span class="dm-step__num">3</span>
        <span class="dm-step__body"><span class="dm-step__title">Review &amp; submit</span><span class="dm-step__sub">Confirm</span></span>
      </div>
    </nav>

    <div v-if="error" class="dm-alert dm-alert--err" style="margin-bottom:16px">{{ error }}</div>
    <div v-if="loading" class="text-muted">Building your mailing…</div>

    <template v-if="loaded">
      <!-- ── Summary cards ── -->
      <div class="sum-grid">
        <div class="sum-card">
          <div class="sum-card__lbl"><CygnusIcon name="users" />Audience</div>
          <div class="sum-card__big">{{ fmt(batch.recipient_count) }}</div>
          <div class="sum-card__sub">recipients after exclusions</div>
          <ul class="sum-list">
            <li><span>Excluded - incomplete address</span><b>{{ fmt(batch.excluded_incomplete) }}</b></li>
            <li><span>Suppressed - do not mail</span><b>{{ fmt(batch.suppressed_count) }}</b></li>
            <li><span>Skipped - mailed in the last 30 days</span><b>{{ fmt(batch.capped_count) }}</b></li>
          </ul>
        </div>

        <div class="sum-card">
          <div class="sum-card__lbl"><CygnusIcon name="file" />Design</div>
          <div class="sum-card__big">{{ formatLabel }}</div>
          <div class="sum-card__sub">{{ formatDims }}</div>
          <ul class="sum-list">
            <li><span>Source</span><b>{{ sourceLabel }}</b></li>
            <li><span>Pages</span><b>{{ isDoubleSided ? '2 (front / back)' : '1' }}</b></li>
          </ul>
        </div>

        <div class="sum-card">
          <div class="sum-card__lbl"><CygnusIcon name="file" />Proof</div>
          <div v-if="isBuilderSource" class="proof-thumb">
            <div class="proof-thumb__vis"><CygnusIcon name="file" /></div>
            <button type="button" class="btn btn-sec btn-sm" @click="openProof"><CygnusIcon name="file" /> Open proof</button>
            <span class="proof-thumb__note">sample record merged</span>
          </div>
          <div v-else class="proof-thumb">
            <div class="proof-thumb__vis"><CygnusIcon name="upload" /></div>
            <span class="proof-thumb__note">Prints as your supplied PDF, with the address block overlaid.</span>
          </div>
        </div>
      </div>

      <!-- ── What happens next ── -->
      <div class="next-panel">
        <span class="next-panel__ic"><CygnusIcon name="info-circle" /></span>
        <div>
          <h4>What happens next</h4>
          <p>Your list and print-ready artwork are sent to our print and fulfilment team. We are finalising an integrated fulfilment partner, so for now a human confirms pricing and dispatch with you by email within 1 working day. Nothing prints until you approve the quote.</p>
          <span class="soon"><CygnusIcon name="clock" /> Integrated fulfilment - coming soon</span>
        </div>
      </div>

      <!-- ── Cost estimate ── -->
      <section class="dm-card">
        <div class="dm-card__head"><CygnusIcon name="coins" class="dm-card__icon" /><h3 class="dm-card__title">Cost estimate</h3><span class="dm-card__subtitle">indicative - confirmed before anything prints</span></div>
        <div class="dm-card__body">
          <table class="cost">
            <thead><tr><th>Line</th><th>Basis</th><th class="num">Unit</th><th class="num">Estimate</th></tr></thead>
            <tbody>
              <tr>
                <td>Print - {{ formatLabel }}</td>
                <td>{{ fmt(batch.recipient_count) }} units</td>
                <td class="num">from {{ fromPence }}p</td>
                <td class="num">{{ printEstimate }}</td>
              </tr>
              <tr><td>Postage - 2nd class, large-batch</td><td>{{ fmt(batch.recipient_count) }} items</td><td class="num">placeholder</td><td class="num">to be quoted</td></tr>
              <tr><td>Fulfilment / handling</td><td>per batch</td><td class="num">placeholder</td><td class="num">to be quoted</td></tr>
            </tbody>
            <tfoot><tr><td colspan="3">Estimated total</td><td class="num">confirmed by email</td></tr></tfoot>
          </table>
          <div class="cost-note"><CygnusIcon name="info-circle" />Print unit price is indicative from our current rate card. Postage and fulfilment are quoted by the team - you approve the total before anything prints.</div>
        </div>
      </section>

      <!-- ── Where scans land (per-recipient QR/PURL destination) ── -->
      <section v-if="!submitted" class="dm-card">
        <div class="dm-card__head"><CygnusIcon name="link" class="dm-card__icon" /><h3 class="dm-card__title">Where scans land</h3><span class="dm-card__subtitle">optional - each piece carries a unique scan link</span></div>
        <div class="dm-card__body">
          <p class="scan-help">Every mail piece gets its own QR / short link. When a recipient scans it, we record the scan against that company and send them to the page you choose. Leave this as the default and they will see a simple hosted "thanks for scanning" page.</p>
          <label class="scan-field">
            <span class="scan-field__lbl">Landing page</span>
            <select v-model="landingPageId" class="scan-select" @change="onLandingChange">
              <option :value="0">Hosted "thanks for scanning" page (default)</option>
              <option v-for="p in landingPages" :key="p.id" :value="p.id">{{ p.title || p.slug }}</option>
            </select>
          </label>
          <p v-if="landingUpdating" class="scan-help scan-help--busy"><CygnusIcon name="clock" /> Updating your mailing…</p>
        </div>
      </section>

      <!-- ── Submit / Buy ── -->
      <div v-if="!submitted" class="dm-action-bar dm-submit-bar">
        <router-link :to="designLink" class="btn btn-sec"><CygnusIcon name="arrow-left" /> Back to design</router-link>
        <span class="dm-action-bar__spacer"></span>

        <template v-if="isPaying">
          <span class="dm-action-bar__note">By submitting you send the list and artwork to fulfilment - no charge yet.</span>
          <button class="btn btn-pri btn-lg" :disabled="submitting" @click="submit">
            <CygnusIcon name="rocket" /> {{ submitting ? 'Submitting…' : 'Submit for print and dispatch' }}
          </button>
        </template>

        <!-- Non-paying: violet one-off checkout (DM-4 - Hermes-hosted payment) -->
        <div v-else class="buy-cta">
          <div class="buy-cta__copy">
            <b>Direct Mail is a one-off purchase.</b>
            <span>Buy this campaign to send it to print - print, postage and a per-record data fee, billed once through Hermes.</span>
          </div>
          <router-link class="btn btn-buy btn-lg" :to="checkoutLink" title="One-off checkout">
            <CygnusIcon name="lock" /> Buy this campaign
          </router-link>
        </div>
      </div>

      <!-- ── Post-submit status timeline (read-only) ── -->
      <section v-if="submitted" class="dm-card dm-timeline-card">
        <div class="dm-card__head"><CygnusIcon name="route" class="dm-card__icon" /><h3 class="dm-card__title">Status</h3><span class="dm-card__subtitle">this mailing - batch #{{ batch.id }}</span></div>
        <div class="dm-card__body">
          <div v-if="batch.status === 'failed'" class="dm-alert dm-alert--err" style="margin-bottom:14px">This mailing could not be processed. Our team has been notified.</div>
          <div class="timeline">
            <div v-for="(step, i) in timelineSteps" :key="step.key" class="tl-step" :class="step.state">
              <div class="tl-rail">
                <div class="tl-dot"><CygnusIcon :name="step.icon" /></div>
                <div v-if="i < timelineSteps.length - 1" class="tl-line"></div>
              </div>
              <div class="tl-body"><div class="tl-title">{{ step.title }}</div><div class="tl-meta">{{ step.meta }}</div></div>
            </div>
          </div>
          <div class="dm-action-bar" style="margin-top:16px">
            <span class="dm-action-bar__spacer"></span>
            <router-link :to="directMailLink" class="btn btn-sec">Back to Direct Mail</router-link>
          </div>
        </div>
      </section>

      <!-- ── Scans (per-recipient QR/PURL closed loop) ── -->
      <section v-if="submitted" class="dm-card dm-scans-card">
        <div class="dm-card__head">
          <CygnusIcon name="chart-bar" class="dm-card__icon" />
          <h3 class="dm-card__title">Scans</h3>
          <span class="dm-card__subtitle">who scanned their mail piece - batch #{{ batch.id }}</span>
          <span v-if="scansSyncing" class="dm-scans-sync"><CygnusIcon name="clock" /> Updating…</span>
        </div>
        <div class="dm-card__body">
          <!-- Empty state (pre-dispatch / no scans yet) -->
          <div v-if="scansReady && !hasScans" class="scan-empty">
            <div class="scan-empty__ic"><CygnusIcon name="link" /></div>
            <p>Scans appear here once your pieces are in the wild - each QR is unique to its recipient.</p>
          </div>

          <template v-else-if="scansReady">
            <!-- Headline stat tiles -->
            <div class="scan-tiles">
              <div class="scan-tile">
                <div class="scan-tile__big">{{ fmt(scans.totals.recipients) }}</div>
                <div class="scan-tile__lbl">Recipients</div>
              </div>
              <div class="scan-tile">
                <div class="scan-tile__big">{{ fmt(scans.totals.scanned) }}</div>
                <div class="scan-tile__lbl">Scanned</div>
              </div>
              <div class="scan-tile">
                <div class="scan-tile__big">{{ scans.totals.scan_rate }}%</div>
                <div class="scan-tile__lbl">Scan rate</div>
              </div>
              <div v-if="scans.submissions > 0" class="scan-tile">
                <div class="scan-tile__big">{{ fmt(scans.submissions) }}</div>
                <div class="scan-tile__lbl">Page submissions</div>
              </div>
            </div>

            <!-- Scans-by-day timeline (CSS bars) -->
            <div v-if="scans.timeline.length" class="scan-tl">
              <div class="scan-tl__head">Scans by day <span>each bar counts every scan that day, including repeats</span></div>
              <div class="scan-tl__bars">
                <div v-for="d in scans.timeline" :key="d.date" class="scan-bar" :title="d.date + ': ' + d.scans + ' scan' + (d.scans === 1 ? '' : 's')">
                  <div class="scan-bar__track"><div class="scan-bar__fill" :style="{ height: barHeight(d.scans) }"></div></div>
                  <div class="scan-bar__n">{{ d.scans || '' }}</div>
                  <div class="scan-bar__day">{{ shortDay(d.date) }}</div>
                </div>
              </div>
            </div>

            <!-- Recipient breakdown -->
            <div class="scan-tbl-wrap">
              <table class="scan-tbl">
                <thead><tr><th>Business</th><th>Status</th><th class="num">Scans</th><th>First scan</th></tr></thead>
                <tbody>
                  <tr v-for="r in scans.recipients.rows" :key="r.company_id">
                    <td class="scan-tbl__biz">{{ r.business }}</td>
                    <td><span class="scan-badge" :class="r.scanned ? 'is-yes' : 'is-no'">{{ r.scanned ? 'Scanned' : 'Not yet' }}</span></td>
                    <td class="num">{{ r.scanned ? fmt(r.scan_count) : '-' }}</td>
                    <td class="scan-tbl__when">{{ r.first_scanned_at ? fmtDate(r.first_scanned_at) : '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="scans.recipients.total_pages > 1" class="scan-pager">
              <button class="btn btn-sec btn-sm" :disabled="scansPage <= 1 || scansSyncing" @click="scanGoto(scansPage - 1)">
                <CygnusIcon name="arrow-left" /> Prev
              </button>
              <span class="scan-pager__meta">Page {{ scans.recipients.page }} of {{ scans.recipients.total_pages }}</span>
              <button class="btn btn-sec btn-sm" :disabled="scansPage >= scans.recipients.total_pages || scansSyncing" @click="scanGoto(scansPage + 1)">
                Next <CygnusIcon name="arrow-right" />
              </button>
            </div>
          </template>

          <div v-else class="text-muted">Loading scan activity…</div>
        </div>
      </section>
    </template>

    <transition name="dm-toast">
      <div v-if="message" class="dm-toast"><CygnusIcon name="check-circle" /> {{ message }}</div>
    </transition>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

/**
 * DirectMailSubmit — "Review and submit" (Direct Mail slice DM-3).
 *
 * Faithful to mockups/direct-mail/submit.html: the audience + design + proof
 * summary cards (with the exclusion breakdown: incomplete address / suppressed /
 * mailed-recently), the honest "what happens next" interim panel, the placeholder
 * cost table, the Submit button (replaced by the violet "Buy this campaign" CTA
 * for non-paying tenants - DM-4 wires the Hermes checkout), and the read-only
 * post-submit status timeline driven by the batch status.
 *
 * On arrival it builds a draft batch from the design's linked audience (fresh
 * recipient maths, server-side), then reads that batch back for the summary. Copy
 * uses plain hyphens (never em dashes).
 */
export default {
  name: "DirectMailSubmit",
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      loading: true,
      loaded: false,
      error: null,
      message: "",
      messageTimer: null,
      isPaying: true,
      submitting: false,
      designId: 0,
      audienceId: 0,
      landingPageId: 0,
      landingPages: [],
      landingUpdating: false,
      batch: {
        id: 0, name: "", format: "postcard_a6", status: "draft", landing_page_id: null,
        recipient_count: 0, excluded_incomplete: 0, suppressed_count: 0, capped_count: 0,
      },
      design: null,
      audience: null,
      formats: [],
      // Scan analytics (slice A4) — derived read model + gentle polling.
      scans: null,
      scansReady: false,
      scansSyncing: false,
      scansPage: 1,
      scanPollTimer: null,
      onVisibility: null,
    };
  },
  computed: {
    submitted() {
      return this.batch.status && this.batch.status !== "draft";
    },
    hasScans() {
      return !!this.scans && Number(this.scans.totals.total_scans) > 0;
    },
    scanBarMax() {
      if (!this.scans || !this.scans.timeline.length) return 0;
      return this.scans.timeline.reduce((m, d) => Math.max(m, Number(d.scans) || 0), 0);
    },
    formatSpec() {
      return this.formats.find((f) => f.key === this.batch.format) || null;
    },
    formatLabel() {
      return this.formatSpec ? this.formatSpec.label : this.batch.format;
    },
    formatDims() {
      return this.formatSpec ? this.formatSpec.dims_label : "";
    },
    isDoubleSided() {
      return this.formatSpec ? !!this.formatSpec.double_sided : false;
    },
    fromPence() {
      return this.formatSpec ? this.formatSpec.from_pence : 0;
    },
    isBuilderSource() {
      return !this.design || (this.design.source || "builder") === "builder";
    },
    isUploadSource() {
      return this.design && this.design.source === "upload";
    },
    sourceLabel() {
      return this.isUploadSource ? "Uploaded PDF" : "Tokenised (built in Yorlin)";
    },
    printEstimate() {
      const n = Number(this.batch.recipient_count) || 0;
      const p = Number(this.fromPence) || 0;
      if (!n || !p) return "to be quoted";
      const pounds = (n * p) / 100;
      return "from £" + pounds.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    audienceLink() {
      return this.audienceId ? { path: "/outreach/direct-mail", query: { audience: this.audienceId } } : { path: "/outreach/direct-mail" };
    },
    designLink() {
      const query = {};
      if (this.audienceId) query.audience = this.audienceId;
      return { path: "/outreach/direct-mail/design", query };
    },
    directMailLink() {
      return { path: "/outreach/direct-mail" };
    },
    checkoutLink() {
      const query = {};
      if (this.batch.id) query.batch = this.batch.id;
      if (this.designId) query.design = this.designId;
      if (this.audienceId) query.audience = this.audienceId;
      return { path: "/outreach/direct-mail/checkout", query };
    },
    timelineSteps() {
      const order = ["submitted", "proof_approved", "printing", "dispatched"];
      const labels = {
        submitted: { title: "Submitted", icon: "check", meta: "Sent to fulfilment" },
        proof_approved: { title: "Proof approved", icon: "clock", meta: "Awaiting your quote approval by email" },
        printing: { title: "Printing", icon: "file", meta: "Not started" },
        dispatched: { title: "Dispatched", icon: "route", meta: "Not started" },
      };
      const currentIdx = order.indexOf(this.batch.status);
      return order.map((key, i) => {
        let state = "";
        if (this.batch.status === "failed") {
          state = i === 0 ? "done" : "";
        } else if (i < currentIdx) {
          state = "done";
        } else if (i === currentIdx) {
          state = "current";
        }
        const meta = state === "done" && key === "submitted" ? "Sent to fulfilment" : labels[key].meta;
        return { key, title: labels[key].title, icon: state === "done" ? "check" : labels[key].icon, meta, state };
      });
    },
  },
  created() {
    const q = this.$route && this.$route.query ? this.$route.query : {};
    this.designId = Number(q.design) || 0;
    this.audienceId = Number(q.audience) || 0;
    if (Number(q.batch) > 0) this.batch.id = Number(q.batch);
    this.boot();
  },
  beforeUnmount() {
    if (this.messageTimer) clearTimeout(this.messageTimer);
    this.stopScanPoll();
  },
  methods: {
    async boot() {
      await Promise.all([this.loadTier(), this.loadLandingPages()]);
      if (this.batch.id > 0) {
        await this.loadBatch(this.batch.id);
      } else {
        await this.buildBatch();
      }
      this.loaded = true;
      this.loading = false;
    },
    async loadLandingPages() {
      try {
        const res = await this.api.get("/api/v1/admin/landing-pages");
        const body = res?.data ?? res;
        const rows = Array.isArray(body?.data) ? body.data : (Array.isArray(body) ? body : []);
        // Only published pages are valid scan destinations (drafts 404 publicly).
        this.landingPages = rows.filter((p) => (p.status || "") === "published");
      } catch (e) {
        /* the selector is optional; default to fallback-only when the list fails */
      }
    },
    async loadTier() {
      try {
        const res = await this.api.get("/api/v1/direct-mail/tier");
        const body = res?.data ?? res;
        this.isPaying = !!body?.is_paying;
        if (Array.isArray(body?.formats)) this.formats = body.formats;
      } catch (e) {
        /* tier is a nicety; default to paying-safe UI + empty format catalogue */
      }
    },
    async buildBatch() {
      if (!this.designId) {
        this.error = "No design selected. Start from the mail-piece builder.";
        this.loading = false;
        return;
      }
      try {
        // Resolve the design's linked audience if not passed on the query.
        if (!this.audienceId) {
          const dRes = await this.api.get("/api/v1/direct-mail/designs/" + this.designId);
          const d = (dRes?.data ?? dRes)?.design;
          if (d && d.audience_id) this.audienceId = d.audience_id;
        }
        const res = await this.api.post("/api/v1/direct-mail/batches", {
          design_id: this.designId,
          audience_id: this.audienceId,
          name: "",
          landing_page_id: this.landingPageId || 0,
        });
        this.applyBatchResponse(res?.data ?? res);
      } catch (e) {
        this.error = this.errText(e, "Could not build the mailing. Make sure the audience and design are ready.");
      }
    },
    async onLandingChange() {
      // landing_page_id is fixed at build time (the batch freezes its recipient
      // maths), so changing the destination rebuilds the still-draft batch with
      // the new choice. A no-op once submitted (the selector is hidden then).
      if (this.submitted || this.landingUpdating) return;
      this.landingUpdating = true;
      try {
        await this.buildBatch();
      } finally {
        this.landingUpdating = false;
      }
    },
    async loadBatch(id) {
      try {
        const res = await this.api.get("/api/v1/direct-mail/batches/" + id);
        this.applyBatchResponse(res?.data ?? res);
      } catch (e) {
        this.error = this.errText(e, "Could not load the mailing.");
      }
    },
    applyBatchResponse(body) {
      if (body?.batch) {
        this.batch = { ...this.batch, ...body.batch };
        // Keep the selector in sync with the batch's frozen destination.
        this.landingPageId = Number(this.batch.landing_page_id) || 0;
      }
      if (body?.design) {
        this.design = body.design;
        if (this.design.audience_id) this.audienceId = this.design.audience_id;
      }
      if (body?.audience) this.audience = body.audience;
      if (this.batch.design_id) this.designId = this.batch.design_id;
    },
    async submit() {
      if (!this.batch.id) return;
      this.submitting = true;
      try {
        const res = await this.api.post("/api/v1/direct-mail/batches/" + this.batch.id + "/submit", {});
        const body = res?.data ?? res;
        this.applyBatchResponse(body);
        this.toast("Submitted to fulfilment.");
      } catch (e) {
        const body = e?.response?.data ?? e?.data;
        if (body?.requires_purchase) {
          // Non-paying: fall back to the Buy CTA (server-enforced tier gate).
          this.isPaying = false;
          if (body?.batch) this.batch = { ...this.batch, ...body.batch };
        } else {
          this.error = this.errText(e, "Could not submit the mailing.");
        }
      } finally {
        this.submitting = false;
      }
    },
    openProof() {
      if (this.designId) window.open("/api/v1/direct-mail/designs/" + this.designId + "/proof.pdf", "_blank");
    },
    fmt(n) {
      return (Number(n) || 0).toLocaleString("en-GB");
    },
    toast(msg) {
      this.message = msg;
      if (this.messageTimer) clearTimeout(this.messageTimer);
      this.messageTimer = setTimeout(() => { this.message = ""; }, 3000);
    },
    errText(e, fallback) {
      const body = e?.response?.data ?? e?.data;
      return body?.message || body?.error || fallback;
    },

    // ── Scan analytics (slice A4) ──────────────────────────────────────────
    beginScans() {
      if (!this.batch.id || this.scanPollTimer) return;
      this.loadScans(1, false);
      // Gentle 30s poll while the tab is open; pause when hidden, refetch on return.
      this.onVisibility = () => { if (!document.hidden) this.loadScans(this.scansPage, true); };
      document.addEventListener("visibilitychange", this.onVisibility);
      this.scanPollTimer = setInterval(() => {
        if (!document.hidden) this.loadScans(this.scansPage, true);
      }, 30000);
    },
    stopScanPoll() {
      if (this.scanPollTimer) { clearInterval(this.scanPollTimer); this.scanPollTimer = null; }
      if (this.onVisibility) { document.removeEventListener("visibilitychange", this.onVisibility); this.onVisibility = null; }
    },
    async loadScans(page, silent) {
      if (!this.batch.id) return;
      this.scansPage = page;
      if (silent) this.scansSyncing = true;
      try {
        const res = await this.api.get(
          "/api/v1/direct-mail/batches/" + this.batch.id + "/scans",
          { params: { page, per_page: 25 } },
        );
        const body = res?.data ?? res;
        if (body && body.totals) {
          this.scans = {
            totals: body.totals,
            submissions: Number(body.submissions) || 0,
            timeline: Array.isArray(body.timeline) ? body.timeline : [],
            recipients: body.recipients || { rows: [], page: 1, per_page: 25, total: 0, total_pages: 0 },
          };
          this.scansReady = true;
        }
      } catch (e) {
        // Analytics is a read-only nicety; a transient failure leaves the last
        // good view in place (or the loading text on the very first attempt).
      } finally {
        this.scansSyncing = false;
      }
    },
    scanGoto(page) {
      if (page < 1) return;
      this.loadScans(page, true);
    },
    barHeight(n) {
      const max = this.scanBarMax;
      if (max <= 0) return "0%";
      const pct = Math.round((Number(n) || 0) / max * 100);
      return (Number(n) > 0 ? Math.max(pct, 6) : 0) + "%";
    },
    shortDay(date) {
      const parts = String(date).split("-");
      return parts.length === 3 ? String(Number(parts[2])) : String(date);
    },
    fmtDate(ts) {
      const d = new Date((Number(ts) || 0) * 1000);
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
        + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    },
  },
  watch: {
    submitted(now) {
      if (now) this.beginScans();
      else this.stopScanPoll();
    },
  },
};
</script>

<style scoped>
.dm-page { max-width: 1100px; }
.text-muted { color: var(--m-text-secondary); }
.dm-head-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.musubi-page__head h1 { font-size: 1.6rem; font-weight: 700; margin: 0 0 6px; display: flex; align-items: center; gap: 10px; }
.musubi-page__head p { margin: 0; max-width: 860px; line-height: 1.55; font-size: 0.92rem; }
.dm-badge { font-size: 0.6rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: #fff; background: var(--m-accent, #4F46E5); border-radius: 999px; padding: 3px 8px; }
.plan-pill { display: inline-flex; align-items: center; gap: 5px; font-size: 0.6rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; border-radius: 999px; padding: 3px 9px; }
.plan-pill--free { color: #6D28D9; background: #F3EEFE; border: 1px solid #E0D2FB; }

/* Stepper */
.dm-steps { display: flex; gap: 10px; margin: 18px 0 20px; flex-wrap: wrap; }
.dm-step { display: flex; align-items: center; gap: 10px; padding: 10px 16px; border: 1px solid var(--m-border); border-radius: 12px; background: var(--m-bg-card); flex: 1; min-width: 200px; text-decoration: none; }
.dm-step.is-current { border-color: var(--m-accent); box-shadow: 0 0 0 1px var(--m-accent) inset; }
.dm-step__num { width: 26px; height: 26px; border-radius: 50%; background: var(--m-accent-soft, #EEF0FF); color: var(--m-accent-hover, #4338CA); font-weight: 700; font-size: 0.82rem; display: flex; align-items: center; justify-content: center; flex: none; }
.dm-step.is-current .dm-step__num { background: var(--m-accent); color: #fff; }
.dm-step.is-done .dm-step__num { background: #DCF2ED; color: #0B6E63; }
.dm-step__title { display: block; font-weight: 600; font-size: 0.86rem; color: var(--m-text-primary); }
.dm-step__sub { display: block; font-size: 0.72rem; color: var(--m-text-tertiary); }

/* Summary cards */
.sum-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 18px; }
@media (max-width: 860px) { .sum-grid { grid-template-columns: 1fr; } }
.sum-card { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 14px; box-shadow: var(--m-shadow-card); padding: 16px; }
.sum-card__lbl { display: flex; align-items: center; gap: 6px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); }
.sum-card__lbl > svg { color: var(--m-accent); }
.sum-card__big { font-size: 1.7rem; font-weight: 800; color: var(--m-text-primary); margin-top: 8px; line-height: 1.1; }
.sum-card__sub { font-size: 0.76rem; color: var(--m-text-secondary); margin-bottom: 10px; }
.sum-list { list-style: none; margin: 0; padding: 10px 0 0; border-top: 1px dashed var(--m-border); }
.sum-list li { display: flex; justify-content: space-between; font-size: 0.8rem; padding: 4px 0; color: var(--m-text-secondary); }
.sum-list li b { color: var(--m-text-primary); }
.proof-thumb { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; margin-top: 8px; }
.proof-thumb__vis { width: 100%; height: 78px; border: 1px dashed var(--m-border); border-radius: 8px; background: linear-gradient(180deg, #FBFCFE, var(--m-bg-card)); display: flex; align-items: center; justify-content: center; color: var(--m-accent); }
.proof-thumb__note { font-size: 0.72rem; color: var(--m-text-tertiary); }

/* What happens next */
.next-panel { display: flex; gap: 12px; align-items: flex-start; background: var(--m-accent-soft, #EEF0FF); border: 1px solid var(--m-border); border-radius: 12px; padding: 14px 16px; margin-bottom: 18px; }
.next-panel__ic { color: var(--m-accent); flex: none; margin-top: 1px; }
.next-panel h4 { margin: 0 0 4px; font-size: 0.92rem; }
.next-panel p { margin: 0; font-size: 0.82rem; line-height: 1.5; color: var(--m-text-secondary); }
.soon { display: inline-flex; align-items: center; gap: 5px; margin-top: 8px; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #6D28D9; background: #F3EEFE; border: 1px solid #E0D2FB; border-radius: 999px; padding: 2px 9px; }
.soon > svg { width: 12px; height: 12px; }

/* Cards */
.dm-card { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 14px; box-shadow: var(--m-shadow-card); margin-bottom: 18px; }
.dm-card__head { display: flex; align-items: center; gap: 9px; padding: 14px 16px; border-bottom: 1px solid var(--m-border); }
.dm-card__icon { color: var(--m-accent); }
.dm-card__title { font-size: 0.92rem; font-weight: 700; margin: 0; }
.dm-card__subtitle { font-size: 0.76rem; color: var(--m-text-tertiary); }
.dm-card__body { padding: 16px; }

/* Where scans land */
.scan-help { font-size: 0.82rem; line-height: 1.5; color: var(--m-text-secondary); margin: 0 0 12px; }
.scan-help--busy { display: inline-flex; align-items: center; gap: 6px; margin: 10px 0 0; color: var(--m-text-tertiary); }
.scan-field { display: flex; flex-direction: column; gap: 6px; max-width: 460px; }
.scan-field__lbl { font-size: 0.76rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); }
.scan-select { font-family: inherit; font-size: 0.86rem; padding: 0.5rem 0.7rem; border: 1px solid var(--m-border); border-radius: 8px; background: var(--m-bg-card); color: var(--m-text-primary); }
.scan-select:focus { outline: none; border-color: var(--m-accent); box-shadow: 0 0 0 1px var(--m-accent) inset; }

/* Cost table */
.cost { width: 100%; border-collapse: collapse; font-size: 0.84rem; }
.cost th, .cost td { text-align: left; padding: 8px 10px; border-bottom: 1px solid var(--m-border); }
.cost th { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); font-weight: 700; }
.cost .num { text-align: right; }
.cost tfoot td { font-weight: 700; color: var(--m-text-primary); border-bottom: none; }
.cost-note { display: flex; align-items: flex-start; gap: 8px; font-size: 0.76rem; color: var(--m-text-tertiary); margin-top: 12px; }
.cost-note > svg { flex: none; margin-top: 1px; }

/* Action bar */
.dm-action-bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.dm-action-bar__note { font-size: 0.78rem; color: var(--m-text-tertiary); }
.dm-action-bar__spacer { flex: 1; }
.dm-submit-bar { margin-bottom: 18px; }

/* Buy CTA (non-paying) */
.buy-cta { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; background: #F7F3FE; border: 1px solid #E0D2FB; border-radius: 12px; padding: 14px 16px; }
.buy-cta__copy { display: flex; flex-direction: column; gap: 2px; font-size: 0.82rem; color: #4C1D95; max-width: 460px; }
.buy-cta__copy b { color: #4C1D95; }

/* Timeline */
.dm-timeline-card { margin-top: 4px; }
.timeline { display: flex; flex-direction: column; }
.tl-step { display: flex; gap: 12px; }
.tl-rail { display: flex; flex-direction: column; align-items: center; }
.tl-dot { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: var(--m-bg-page, #F4F5F9); border: 1px solid var(--m-border); color: var(--m-text-tertiary); flex: none; }
.tl-line { width: 2px; flex: 1; min-height: 22px; background: var(--m-border); margin: 2px 0; }
.tl-step.done .tl-dot { background: #DCF2ED; border-color: #B6EBE4; color: #0B6E63; }
.tl-step.current .tl-dot { background: var(--m-accent); border-color: var(--m-accent); color: #fff; }
.tl-body { padding-bottom: 14px; }
.tl-title { font-size: 0.88rem; font-weight: 600; color: var(--m-text-primary); }
.tl-meta { font-size: 0.76rem; color: var(--m-text-tertiary); }

/* Scans panel */
.dm-scans-card .dm-card__head { position: relative; }
.dm-scans-sync { margin-left: auto; display: inline-flex; align-items: center; gap: 5px; font-size: 0.72rem; color: var(--m-text-tertiary); }
.dm-scans-sync > svg { width: 12px; height: 12px; }
.scan-empty { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 12px; padding: 26px 20px; }
.scan-empty__ic { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; background: var(--m-accent-soft, #EEF0FF); color: var(--m-accent); }
.scan-empty p { margin: 0; max-width: 380px; font-size: 0.86rem; line-height: 1.5; color: var(--m-text-secondary); }

.scan-tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 18px; }
@media (max-width: 700px) { .scan-tiles { grid-template-columns: repeat(2, 1fr); } }
.scan-tile { background: var(--m-bg-page, #F8FAFC); border: 1px solid var(--m-border); border-radius: 12px; padding: 14px 16px; }
.scan-tile__big { font-size: 1.6rem; font-weight: 800; color: var(--m-text-primary); line-height: 1.1; font-variant-numeric: tabular-nums; }
.scan-tile__lbl { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); margin-top: 4px; }

.scan-tl { margin: 0 0 20px; }
.scan-tl__head { font-size: 0.8rem; font-weight: 700; color: var(--m-text-secondary); margin-bottom: 10px; }
.scan-tl__head span { display: block; font-weight: 400; font-size: 0.72rem; color: var(--m-text-tertiary); margin-top: 2px; }
.scan-tl__bars { display: flex; align-items: flex-end; gap: 6px; height: 120px; padding-top: 4px; overflow-x: auto; }
.scan-bar { flex: 1 1 0; min-width: 16px; display: flex; flex-direction: column; align-items: center; height: 100%; }
.scan-bar__track { flex: 1; width: 100%; max-width: 30px; display: flex; align-items: flex-end; }
.scan-bar__fill { width: 100%; min-height: 0; background: var(--m-accent, #4F46E5); border-radius: 4px 4px 0 0; transition: height 0.3s ease; }
.scan-bar__n { font-size: 0.66rem; color: var(--m-text-tertiary); font-variant-numeric: tabular-nums; height: 12px; }
.scan-bar__day { font-size: 0.66rem; color: var(--m-text-tertiary); margin-top: 3px; font-variant-numeric: tabular-nums; }

.scan-tbl-wrap { overflow-x: auto; border: 1px solid var(--m-border); border-radius: 10px; }
.scan-tbl { width: 100%; border-collapse: collapse; font-size: 0.84rem; }
.scan-tbl th, .scan-tbl td { text-align: left; padding: 9px 12px; border-bottom: 1px solid var(--m-border); white-space: nowrap; }
.scan-tbl tbody tr:last-child td { border-bottom: none; }
.scan-tbl th { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); font-weight: 700; }
.scan-tbl .num { text-align: right; font-variant-numeric: tabular-nums; }
.scan-tbl__biz { font-weight: 600; color: var(--m-text-primary); white-space: normal; }
.scan-tbl__when { color: var(--m-text-secondary); }
.scan-badge { display: inline-block; font-size: 0.7rem; font-weight: 700; border-radius: 999px; padding: 2px 9px; }
.scan-badge.is-yes { color: #0B6E63; background: #DCF2ED; border: 1px solid #B6EBE4; }
.scan-badge.is-no { color: var(--m-text-tertiary); background: var(--m-bg-page, #F1F5F9); border: 1px solid var(--m-border); }

.scan-pager { display: flex; align-items: center; justify-content: center; gap: 14px; margin-top: 14px; }
.scan-pager__meta { font-size: 0.78rem; color: var(--m-text-tertiary); }

/* Buttons */
.btn { font-family: inherit; font-weight: 600; font-size: 0.82rem; padding: 0.5rem 0.95rem; border-radius: 8px; border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; text-decoration: none; line-height: 1.3; white-space: nowrap; }
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-sm { font-size: 0.76rem; padding: 0.35rem 0.7rem; }
.btn-lg { font-size: 0.9rem; padding: 0.6rem 1.2rem; }
.btn-pri { background: var(--m-accent); border-color: var(--m-accent); color: #fff; }
.btn-pri:hover:not(:disabled) { background: var(--m-accent-hover); }
.btn-sec { color: var(--m-text-secondary); border-color: var(--m-border); background: var(--m-bg-card); }
.btn-sec:hover:not(:disabled) { background: var(--m-accent-soft); border-color: var(--m-accent); color: var(--m-text-primary); }
.btn-buy { background: #7C3AED; border-color: #7C3AED; color: #fff; }

/* Alert / toast */
.dm-alert { padding: 9px 12px; font-size: 0.8rem; border-radius: 8px; }
.dm-alert--err { background: #FCECEC; color: #B42318; border: 1px solid #F5C9C4; }
.dm-toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); background: #0B6E63; color: #fff; padding: 10px 18px; border-radius: 999px; font-size: 0.84rem; font-weight: 600; display: flex; align-items: center; gap: 8px; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2); z-index: 1100; }
.dm-toast-enter-active, .dm-toast-leave-active { transition: opacity 0.25s, transform 0.25s; }
.dm-toast-enter-from, .dm-toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }
</style>
